import { Flatfile } from '@flatfile/api';
import type {
  VisitorType,
} from 'application-types';

import { BASE_FIELD_KEYS, type FormCustomFieldColumn } from './workbook';

interface PhoneType {
  countryCode?: string;
  cellNumber?: string;
}

const isEmpty = (val: unknown) =>
  val === null || val === undefined || val === '';

/** Sheet defines custom columns that are not backed by form custom-field config (ingest contract violation). */
export class CustomFieldSheetMappingError extends Error {
  readonly fieldKeys: readonly string[];

  constructor(fieldKeys: readonly string[]) {
    super(
      `Flatfile sheet has custom columns without customFieldConfigId mapping: ${fieldKeys.join(', ')}`
    );
    this.name = 'CustomFieldSheetMappingError';
    this.fieldKeys = fieldKeys;
  }
}

function assertCustomColumnsMappedToConfig(
  sheet: Flatfile.Sheet | undefined,
  fieldKeyToConfigId: Map<string, string>
): void {
  const fields = sheet?.config?.fields;

  if (!fields?.length) {
    return;
  }

  const unmappedKeys: string[] = [];

  for (const field of fields) {
    const fieldKey = field.key;

    if (!fieldKey || BASE_FIELD_KEYS.has(fieldKey)) {
      continue;
    }

    if (!fieldKeyToConfigId.has(fieldKey)) {
      unmappedKeys.push(fieldKey);
    }
  }

  if (unmappedKeys.length > 0) {
    throw new CustomFieldSheetMappingError(unmappedKeys);
  }
}

function assignBaseVisitorField(
  fieldKey: string,
  recordValue: string,
  visitor: VisitorType,
  phone: PhoneType
): boolean {
  if (!BASE_FIELD_KEYS.has(fieldKey)) {
    return false;
  }

  switch (fieldKey) {
    case 'email':
      visitor.email = recordValue;
      break;
    case 'firstName':
      visitor.firstName = recordValue;
      break;
    case 'lastName':
      visitor.lastName = recordValue;
      break;
    case 'company':
      visitor.company = recordValue;
      break;
    case 'countryCode':
      phone.countryCode = recordValue;
      break;
    case 'cellNumber':
      phone.cellNumber = recordValue;
      break;
    default:
      break;
  }

  return true;
}

export const processVisitorData = (
  sheet: Flatfile.Sheet | undefined,
  sheetDataRecords: Flatfile.RecordsWithLinks,
  formCustomFields: FormCustomFieldColumn[] = []
): VisitorType[] => {
  const fieldKeyToConfigId = new Map(
    formCustomFields.map(column => [column.fieldKey, column.customFieldConfigId])
  );
  const fieldKeyToFieldType = new Map(
    formCustomFields.map(column => [column.fieldKey, column.fieldType])
  );

  assertCustomColumnsMappedToConfig(sheet, fieldKeyToConfigId);


  return sheetDataRecords
    .filter((record: Flatfile.RecordWithLinks) => record.valid)
    .map((record: Flatfile.RecordWithLinks) => {
      const visitor: VisitorType = {};
      const phone: PhoneType = {};
      const customFieldValues: NonNullable<VisitorType['customFieldValues']> =
        [];

      sheet?.config.fields.forEach((field: Flatfile.Property) => {
        const fieldKey = field.key;
        const cell = record.values[fieldKey];
        const rawValue = cell?.value;
        const recordValue =
          rawValue === null || rawValue === undefined ? '' : String(rawValue);

        if (assignBaseVisitorField(fieldKey, recordValue, visitor, phone)) {
          return;
        }

        const customFieldConfigId = fieldKeyToConfigId.get(fieldKey);

        if (customFieldConfigId === undefined) {
          throw new CustomFieldSheetMappingError([fieldKey]);
        }

        const fieldType = fieldKeyToFieldType.get(fieldKey);
        const value =
          fieldType === 'multi_select'
            ? recordValue.split(',').map(v => v.trim()).filter(Boolean)
            : [recordValue];

        customFieldValues.push({ customFieldConfigId, value });
      });

      visitor.phone =
        !isEmpty(phone.countryCode) || !isEmpty(phone.cellNumber)
          ? `+${phone.countryCode}${phone.cellNumber}`
          : '';

      if (customFieldValues.length > 0) {
        visitor.customFieldValues = customFieldValues;
      }

      return visitor;
    });
};
